import { useEffect, useState, type FormEvent } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router";
import { useAuth } from "../auth/AuthProvider";
import { Trans, useTranslation } from "../i18n";
import { SocialSignIn } from "../components/SocialSignIn";
import { AuthShell, LegalNotice, PasswordInput, PasswordStrength } from "../components/AuthShell";
import { TURNSTILE_SITE_KEY, Turnstile } from "../components/Turnstile";
import { Alert, Button, Field, Input } from "../components/ui";
import { api, ApiError } from "../lib/api";
import { track } from "../lib/gtm";
import { CheckYourEmail } from "./CheckYourEmail";

const MIN_PASSWORD_LENGTH = 8;

function destinationFrom(state: unknown): string {
  const from =
    state && typeof state === "object" && "from" in state && typeof state.from === "string"
      ? state.from
      : null;
  return from?.startsWith("/") && !from.startsWith("//") ? from : "/apps";
}

/**
 * A promotion code carried in from wherever the visitor came from.
 *
 * The marketing site's offer page links here with `?promo=CODE`, which is the
 * whole of "signs up automatically with the campaign": the visitor reads an
 * offer, presses a button, and never has to notice there was a code at all.
 *
 * Sanitised rather than trusted, because it arrives in a URL anybody can write.
 * Codes are alphanumeric with dashes — the campaign column allows 3 to 40
 * characters — so anything else is either a mistake or somebody trying to get
 * markup into a field this page renders. A value that does not fit is dropped
 * entirely instead of trimmed into something that looks deliberate: an empty
 * field is a state the form already handles, and a mangled code is not.
 */
export function promoFromQuery(search: string): string {
  const raw = new URLSearchParams(search).get("promo")?.trim().toUpperCase() ?? "";
  return /^[A-Z0-9-]{3,40}$/.test(raw) ? raw : "";
}

/**
 * Whether this visitor arrived from the free-inference campaign page.
 *
 * The same handoff as `?promo=`, and for the same reason: somebody reads an
 * offer, presses a button, and the form they land on already knows what they
 * came for. `?free-ai=1` is what the campaign page's call to action appends.
 *
 * It only decides whether to *ask*. A visitor who writes the parameter by hand
 * gets the same question and the same answer as anyone else, because what the
 * box does is park a request that an operator still has to approve.
 */
export function freeAiFromQuery(search: string): boolean {
  return new URLSearchParams(search).get("free-ai") === "1";
}

/**
 * The campaign, as the box needs it: what to call the offer, and whether there
 * is one. The endpoint says more -- the size of the allowance among it -- and
 * this deliberately does not read it. See the box itself.
 */
interface PublicCampaign {
  name: string;
}

/**
 * The affiliate code a tracked link carried here, and the *fallback* half of
 * how attribution reaches the API.
 *
 * The primary half is a cookie the marketing site sets on the registrable
 * domain, which `POST /v1/auth/register` reads server-side without this page
 * being involved at all. That cookie is written only where the visitor accepted
 * functional storage, so this is what credits an affiliate when they did not:
 * the marketing site rewrites every link into the dashboard to carry `?ref=`,
 * and the value rides through here into the request body.
 *
 * Never displayed and never editable. Unlike a promotion code — which is an
 * offer the visitor is claiming and should see — this is a fact about where they
 * came from, and putting it in a field would invite somebody to change it or
 * wonder what it is.
 *
 * Case is preserved, which is the one place this differs from `promoFromQuery`.
 * Codes are `citext` in the affiliate service, so `MaxLayerAna` and `maxlayerana`
 * resolve to the same link either way; upper-casing would only make a log line
 * disagree with the link the affiliate is looking at.
 */
export function referralFromQuery(search: string): string {
  const raw = new URLSearchParams(search).get("ref")?.trim() ?? "";
  return /^[A-Za-z0-9][A-Za-z0-9_-]{2,39}$/.test(raw) ? raw : "";
}

export function RegisterPage() {
  const { status, register } = useAuth();
  const { t } = useTranslation("auth");
  const location = useLocation();
  const navigate = useNavigate();
  const destination = destinationFrom(location.state);
  // Read once, at mount. A lazy initialiser rather than an effect: the field is
  // filled before first paint, so nobody sees it empty and then fill itself.
  const [linkedPromo] = useState(() => promoFromQuery(location.search));
  /*
   * Read once at mount, like the promotion code above and for the same reason —
   * `location.search` is stable, and reading it in an effect would mean the
   * first submit could race the read.
   *
   * Held outside `form` because it is not a field: nothing renders it, nothing
   * can change it, and putting it in the form state would make it look like one
   * to the next person editing this file.
   */
  const [linkedReferral] = useState(() => referralFromQuery(location.search));
  const [form, setForm] = useState({
    name: "",
    organizationName: "",
    promoCode: linkedPromo,
    email: "",
    password: "",
  });

  /*
   * The campaign, fetched only for visitors who arrived from its page.
   *
   * An ordinary signup makes no extra request: `askedForFreeAi` is false and
   * the effect returns immediately. When it is true the public endpoint says
   * whether anything is actually running, so the box can name the offer rather
   * than promise one that may have ended while the page was open.
   *
   * A failure leaves it null and the box simply does not appear. Nothing on
   * this screen should be able to stop somebody creating an account.
   */
  const [askedForFreeAi] = useState(() => freeAiFromQuery(location.search));
  const [campaign, setCampaign] = useState<PublicCampaign | null>(null);
  const [wantsFreeAi, setWantsFreeAi] = useState(askedForFreeAi);

  useEffect(() => {
    if (!askedForFreeAi) return;
    let cancelled = false;

    void (async () => {
      try {
        const result = await api<{ data: (PublicCampaign & { open: boolean }) | null }>(
          "/v1/promotions/inference-campaign",
          /* No cookie. This is the wide-open endpoint the campaign page reads
             too, so it answers with a wildcard origin, and a browser rejects
             that on a credentialed request -- which is not a smaller failure
             than a missing campaign, it is this box never appearing at all. */
          { anonymous: true, omitCredentials: true },
        );
        if (!cancelled && result.data?.open) setCampaign(result.data);
      } catch {
        /* No offer shown. The account is what this page is for. */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [askedForFreeAi]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  /*
   * The Turnstile token, where this deployment asks for one. The button waits
   * for it -- the challenge takes a moment and asks nothing, so the wait is a
   * second's greyed button rather than a step. A token is good for one
   * request; `challenge` is bumped after a refused signup so the widget
   * issues another before the form can be sent again.
   */
  const [humanToken, setHumanToken] = useState<string | null>(null);
  const [challenge, setChallenge] = useState(0);
  /*
   * Set when the API held the account pending confirmation. Kept in state rather
   * than routed to, because there is no session yet and nothing to protect a
   * `/check-your-email` URL from being opened cold by anybody — a screen that
   * names an address should only appear to whoever just typed it.
   */
  const [pending, setPending] = useState<{ email: string; emailSent: boolean } | null>(null);

  if (status === "authenticated") return <Navigate to={destination} replace />;

  if (pending) {
    return <CheckYourEmail email={pending.email} emailSent={pending.emailSent} />;
  }

  const passwordTooShort = form.password.length > 0 && form.password.length < MIN_PASSWORD_LENGTH;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (passwordTooShort) return;
    setError(null);
    setSubmitting(true);
    try {
      /*
       * The referral code goes with the form and is not part of it. Omitted
       * entirely when there is none, rather than sent as an empty string: the
       * API treats "" as absent anyway, and a body that says `referralCode: ""`
       * on every signup is a field somebody will later mistake for a bug.
       */
      const applying = Boolean(campaign && wantsFreeAi);
      const outcome = await register({
        ...form,
        /* Only when there is something to apply to. A true here against no
           running campaign would park a request that can never be claimed. */
        ...(applying ? { freeInference: true } : {}),
        ...(linkedReferral ? { referralCode: linkedReferral } : {}),
        ...(humanToken ? { turnstileToken: humanToken } : {}),
      });

      /*
       * The ask, counted where it is now made. This fired from the dashboard
       * button that used to exist; the account it belongs to is being created
       * by the call above, so there is no organization id to carry yet and the
       * field is left out rather than filled with something invented.
       */
      if (applying) track("free_inference_request", { object: "api_key" });
      /*
       * Two endings, and only one of them is a navigation. A deployment with no
       * email configured signs the customer straight in, exactly as before; one
       * that can send mail holds the account until the address answers.
       */
      if (outcome.kind === "verification-pending") {
        setPending({ email: outcome.email, emailSent: outcome.emailSent });
        return;
      }
      void navigate(destination, { replace: true });
    } catch (cause) {
      // The one refusal a real person hits in the language they chose: a
      // throwaway-mail domain. Everything else is the API's own sentence.
      setError(
        cause instanceof ApiError && cause.code === "email_domain_blocked"
          ? t("register.tempEmail")
          : cause instanceof ApiError
            ? cause.message
            : t("register.failed"),
      );
      setChallenge((n) => n + 1);
    } finally {
      setSubmitting(false);
    }
  }

  function update(field: keyof typeof form) {
    return (event: React.ChangeEvent<HTMLInputElement>) =>
      setForm((current) => ({ ...current, [field]: event.target.value }));
  }

  return (
    <AuthShell
      title={t("register.title")}
      /* The one key is the product's whole argument, and this is the first
         screen where it is true of the reader rather than of the platform: the
         organization created here issues the key, and that key is what deploys
         and what authorizes inference. Both halves, stated before they have
         typed anything. */
      subtitle={t("register.subtitle")}
      footer={
        <>
          {t("register.haveAccount")}{" "}
          <Link
            className="text-accent transition-colors duration-200 ease-smooth hover:text-accent-hover"
            /*
             * The query string travels with the link.
             *
             * Without it a visitor who lands on `/register?ref=ana`, clicks
             * through to sign in, then comes back to create an account arrives
             * with no code — and attribution falls back to the marketing site's
             * cookie, which only exists where the visitor accepted functional
             * storage. An affiliate who linked straight to the dashboard has no
             * cookie at all, so that round trip lost the commission outright.
             *
             * The whole search rather than just `ref`: `promo` rides here too
             * and has the identical problem.
             */
            to={{ pathname: "/login", search: location.search }}
            state={location.state}
          >
            {t("register.signIn")}
          </Link>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {error && <Alert>{error}</Alert>}

        {/* First, because it is where the typing is saved: a person with a
            GitHub account fills nothing in below. The divider says the form
            is the other way, not a second step. */}
        <SocialSignIn returnTo="/apps" mode="register" placement="above" />

        <div className="space-y-4">
          <p className="micro text-accent">{t("register.yourAccount")}</p>
          <Field label={t("register.name")} htmlFor="name">
            <Input
              id="name"
              size="xl"
              surface="canvas"
              autoComplete="name"
              required
              autoFocus
              value={form.name}
              onChange={update("name")}
            />
          </Field>

          <Field label={t("register.email")} htmlFor="email">
            <Input
              id="email"
              size="xl"
              surface="canvas"
              type="email"
              autoComplete="username"
              required
              value={form.email}
              onChange={update("email")}
            />
          </Field>

          <Field
            label={t("register.password")}
            htmlFor="password"
            hint={t("register.passwordHint", { min: MIN_PASSWORD_LENGTH })}
            error={passwordTooShort ? t("register.passwordTooShort", { min: MIN_PASSWORD_LENGTH }) : null}
          >
            <PasswordInput
              id="password"
              autoComplete="new-password"
              minLength={MIN_PASSWORD_LENGTH}
              value={form.password}
              onChange={update("password")}
            />
            <PasswordStrength password={form.password} minLength={MIN_PASSWORD_LENGTH} />
          </Field>
        </div>

        <div className="space-y-4 border-t border-line pt-5">
          <p className="micro text-accent">{t("register.firstWorkspace")}</p>
          <Field label={t("register.organization")} htmlFor="org" hint={t("register.organizationHint")}>
            <Input
              id="org"
              size="xl"
              surface="canvas"
              autoComplete="organization"
              required
              value={form.organizationName}
              onChange={update("organizationName")}
            />
          </Field>

          {/*
            No promotion code field, and the code still travels.

            There was an optional input here. It asked every visitor for
            something almost none of them have — a form's required fields are the
            account, and an empty box labelled "Promotion code" reads as a
            discount somebody else got. The offer page is where a code comes
            from now, and it attaches itself to the link.

            The value is still submitted; only the input is gone. `promoCode`
            holds whatever `?promo=` carried, so somebody who pressed "Claim
            $300" gets it applied without ever seeing a field.

            Anyone holding a code who did not arrive by that link enters it on
            the billing page, where a refusal can be shown properly — this form
            has no room to explain "for new customers only" or "already used with
            this address", and would have to render a redemption failure as a
            failed signup.
          */}
          {linkedPromo && (
            /*
              Shown only when a code actually arrived, because the person it is
              for pressed a button that promised money and needs to see it
              carried. Text rather than a disabled input: there is nothing to
              edit, and a greyed-out box invites somebody to try.

              It says when the credit lands, which is the support ticket this
              prevents — the grant is released on email confirmation, so nothing
              appears on the balance before then.
            */
            <p className="rounded-md border border-line bg-raised px-3 py-2.5 text-sm text-fg-muted">
              <Trans
                t={t}
                i18nKey="register.offerAttached"
                values={{ code: linkedPromo }}
                components={{ code: <span className="font-mono text-fg" /> }}
              />
            </p>
          )}

          {/*
            The free-inference application, and the only place it can be made.

            A checkbox rather than a notice, because unlike the promotion above
            this is a request somebody can be turned down for — it is applied
            for, not attached. Ticked on arrival, since pressing "Create an
            account" on the campaign page is already the answer; a visitor who
            changed their mind between two pages can untick it.

            It says what happens next in the order it happens: confirm the
            address, then wait. The review is the part people write in about, so
            it is named rather than implied.

            No figure for the allowance, though the endpoint carries one. This
            is a box somebody ticks on the way past, on a form whose job is an
            account -- the number is a thing to weigh, and there is nothing to
            weigh here because applying costs nothing and the answer comes
            later. It belongs where the offer is actually read: the campaign
            page, which is where they came from, and the dashboard card, which
            is where an approval lands.
          */}
          {campaign && (
            <label className="flex cursor-pointer gap-3 rounded-md border border-line bg-raised px-3 py-3">
              <input
                type="checkbox"
                className="mt-0.5 size-4 shrink-0 accent-accent"
                checked={wantsFreeAi}
                onChange={(e) => setWantsFreeAi(e.target.checked)}
              />
              <span className="text-sm">
                <span className="text-fg">{t("register.applyFor", { name: campaign.name })}</span>
                <span className="mt-0.5 block text-fg-muted">
                  {t("register.applyBody")}
                </span>
              </span>
            </label>
          )}
        </div>

        <Turnstile onToken={setHumanToken} generation={challenge} />

        <Button
          type="submit"
          size="xl"
          loading={submitting}
          disabled={Boolean(TURNSTILE_SITE_KEY) && !humanToken}
          className="w-full"
        >
          {t("register.submit")}
        </Button>


        <LegalNotice action="creatingAccount" />
      </form>
    </AuthShell>
  );
}
